<template>
  <div
    class="data-grid-wrapper def-data-grid"
    :class="wrapperClass"
    :tabindex="tabIndex"
    :aria-label="ariaLabel"
    :role="ariaRole"
  >
    <table
      class="data-grid"
      :class="tableClass"
      :aria-label="ariaLabel"
      :role="tableRole"
    >
      <thead v-if="$slots.header">
        <slot name="header" />
      </thead>
      <tbody>
        <tr
          v-for="(item, idx) in items"
          :key="item.id || idx"
          :class="['data-grid-row', getRowClass(item, idx)]"
          role="row"
          @click="$emit('row-clicked', item)"
        >
          <slot name="row" :item="item" :index="idx" :rowActions="rowActions" />
        </tr>
        <tr v-if="!items || items.length === 0">
          <td class="text-center w-100 py-4">
            <slot name="empty" v-if="!loading && !error">{{ $t(noDataText) }}</slot>
            <span v-else-if="loading">Loading...</span>
            <span v-else-if="error">{{ error }}</span>
          </td>
        </tr>
      </tbody>
      <tfoot v-if="$slots['custom-foot']">
        <tr>
          <td>
            <slot name="custom-foot" />
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
export default {
  name: 'DataGrid',
  inheritAttrs: false,
  props: {
    items: {
      type: Array,
      required: true,
      default: () => [],
    },
    ariaLabel: {
      type: String,
      default: 'Data grid',
    },
    ariaRole: {
      type: String,
      default: 'region',
    },
    tableRole: {
      type: String,
      default: 'table',
    },
    tabIndex: {
      type: [String, Number],
      default: 0,
    },
    noDataText: {
      type: String,
      default: 'table.noData',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: [String, Boolean],
      default: '',
    },
    rowActions: {
      type: Array,
      default: () => [],
    },
    wrapperClass: {
      type: [String, Array, Object],
      default: '',
    },
    tableClass: {
      type: [String, Array, Object],
      default: '',
    },
    rowClass: {
      type: [String, Function],
      default: '',
    },
    cellClass: {
      type: [String, Function],
      default: '',
    },
    selectedId: {
      type: [String, Number],
      default: null,
    },
  },
  methods: {
    getRowClass(item, idx) {
      let baseClass = typeof this.rowClass === 'function' ? this.rowClass(item, idx) : this.rowClass;
      if (this.selectedId !== null && (item.id === this.selectedId)) {
        return [baseClass, 'selected-row'].filter(Boolean).join(' ');
      }
      return baseClass;
    },
    getCellClass(field, item, idx) {
      if (typeof this.cellClass === 'function') {
        return this.cellClass(field, item, idx);
      }
      return this.cellClass || (field && field.cellClass) || '';
    },
  },
};
</script>

<style scoped>
.data-grid-wrapper {
  max-width: 100%;
  outline: none;
}
.data-grid {
  width: 100%;
  border-collapse: collapse;
}
.data-grid th,
.data-grid td {
  padding: 0.75em;
  border-bottom: 1px solid #ddd;
  text-align: left;
}
.data-grid th {
  font-weight: bold;
}
tr:focus-within {
  outline: 2px solid #0078d4;
}
.text-center {
  text-align: center;
}
.w-100 {
  width: 100%;
}
.py-4 {
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
}

.data-grid-row {
  display: table-row;
  flex: 1 0 360px;
  gap: 10px;
  text-align: left;
  align-items: stretch;
  transition: background-color 0.3s ease, transform 0.3s ease;
  height: 100%;
}
.data-grid-row:hover {
  transform: scale(1.02);
  cursor: pointer;
}
</style>
